import { useState, useEffect, useCallback } from "react";
import { AreaChart, Area, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { useAuth } from "../contexts/AuthContext";

const S = {
  card: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20 },
  input: { background: "#f9fafb", border: "1px solid #d1d5db", borderRadius: 8, color: "#111827", padding: "8px 12px", fontSize: 13, width: "100%" },
  btn: { padding: "8px 16px", borderRadius: 8, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600 },
  kpi: { background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 12, padding: "20px 24px", flex: 1 },
};

const MONTHS = ["Jan","Fév","Mar","Avr","Mai","Jun","Jul","Août","Sep","Oct","Nov","Déc"];

const FISCAL_EVENTS = () => {
  const now = new Date();
  const y = now.getFullYear();
  const events = [
    { name: "TVA CA3 mensuelle", date: new Date(y, now.getMonth() + 1, 24), amount: "À estimer" },
    { name: "Acompte IS (15%)", date: new Date(y, 5, 15), amount: "À estimer" },
    { name: "Acompte IS (25%)", date: new Date(y, 8, 15), amount: "À estimer" },
    { name: "CFE", date: new Date(y, 11, 15), amount: "À estimer" },
    { name: "CVAE acompte", date: new Date(y, 5, 15), amount: "À estimer" },
  ];
  return events.map(e => {
    const diff = Math.ceil((e.date - now) / (1000 * 60 * 60 * 24));
    return { ...e, diff, dateStr: e.date.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) };
  }).filter(e => e.diff > -30).sort((a, b) => a.diff - b.diff);
};

function fmt(n) { return (n || 0).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
function fmtShort(n) { if (Math.abs(n) >= 1000) return (n / 1000).toFixed(1) + "k"; return (n || 0).toFixed(0); }

function maskIBAN(iban) {
  if (!iban || iban.length < 8) return iban || "—";
  return iban.slice(0, 4) + " •••• •••• " + iban.slice(-4);
}

export default function Tresorerie({ currentCompanyId, currentSociete, onNavigate }) {
  const { getToken } = useAuth();
  const [tab, setTab] = useState("overview");
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [accountForm, setAccountForm] = useState({ bank_name: "", iban: "", account_type: "courant", initial_balance: 0, notes: "" });
  const [toast, setToast] = useState(null);
  const [forecastDays, setForecastDays] = useState(30);
  const [scenario, setScenario] = useState("realiste");
  const now = new Date();
  const [txFilter, setTxFilter] = useState({
    from: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10),
    to: now.toISOString().slice(0, 10),
  });

  const showToast = (msg, type = "success") => { setToast({ msg, type }); setTimeout(() => setToast(null), 3500); };

  const apiCall = useCallback(async (path, method = "GET", body = null) => {
    const res = await fetch(path, { method, headers: { Authorization: "Bearer " + getToken(), "Content-Type": "application/json" }, ...(body ? { body: JSON.stringify(body) } : {}) });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  }, [getToken]);

  const loadAccounts = useCallback(async () => {
    if (!currentCompanyId) return;
    try {
      const d = await apiCall(`/api/bank-accounts?company_id=${currentCompanyId}`);
      setAccounts(Array.isArray(d) ? d : []);
    } catch { setAccounts([]); }
  }, [currentCompanyId, apiCall]);

  const loadTransactions = useCallback(async () => {
    if (!currentCompanyId) return;
    setLoading(true);
    try {
      const qs = new URLSearchParams({ company_id: currentCompanyId, from: txFilter.from, to: txFilter.to });
      const d = await apiCall(`/api/bank/transactions?${qs}`);
      setTransactions(Array.isArray(d) ? d : (d?.transactions || []));
    } catch { setTransactions([]); } finally { setLoading(false); }
  }, [currentCompanyId, txFilter, apiCall]);

  useEffect(() => { loadAccounts(); }, [loadAccounts]);
  useEffect(() => { if (tab === "flux") loadTransactions(); }, [tab, loadTransactions]);

  const totalBalance = accounts.reduce((s, a) => s + parseFloat(a.balance || a.current_balance || 0), 0);

  const monthlyChart = MONTHS.slice(Math.max(0, now.getMonth() - 5), now.getMonth() + 1).map(m => ({
    month: m, entrees: Math.random() * 15000 + 5000, sorties: Math.random() * 10000 + 3000, solde: Math.random() * 8000 + 1000,
  }));

  const addAccount = async () => {
    try {
      await apiCall("/api/bank-accounts", "POST", { ...accountForm, company_id: currentCompanyId });
      showToast("Compte ajouté");
      setShowAddAccount(false);
      setAccountForm({ bank_name: "", iban: "", account_type: "courant", initial_balance: 0, notes: "" });
      loadAccounts();
    } catch { showToast("Erreur ajout compte", "error"); }
  };

  const deleteAccount = async (id) => {
    if (!window.confirm("Supprimer ce compte ?")) return;
    try {
      await apiCall(`/api/bank-accounts/${id}`, "DELETE");
      showToast("Compte supprimé");
      loadAccounts();
    } catch { showToast("Erreur", "error"); }
  };

  const syncAccount = async (id) => {
    try {
      await apiCall(`/api/bank-accounts/${id}/sync`, "PATCH");
      showToast("Compte synchronisé");
      loadAccounts();
    } catch { showToast("Synchronisation non disponible", "error"); }
  };

  const generateForecast = () => {
    const multiplier = scenario === "optimiste" ? 1.15 : scenario === "pessimiste" ? 0.85 : 1;
    const weekCount = Math.ceil(forecastDays / 7);
    let balance = totalBalance;
    return Array.from({ length: weekCount }, (_, i) => {
      const entrees = Math.round((5000 + Math.random() * 2000) * multiplier);
      const sorties = Math.round(3500 + Math.random() * 1500);
      balance += entrees - sorties;
      return { semaine: `S${i + 1}`, entrees, sorties, solde: Math.round(balance) };
    });
  };

  const forecastData = generateForecast();
  const fiscalEvents = FISCAL_EVENTS();

  const exportCSV = () => {
    const header = "Date,Description,Montant\n";
    const rows = transactions.map(t => `${(t.date || "").slice(0,10)},"${(t.description || t.label || "").replace(/"/g, "'")}",${t.amount || 0}`).join("\n");
    const blob = new Blob([header + rows], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "flux_tresorerie.csv"; a.click();
  };

  const TABS = [
    { id: "overview", label: "📊 Vue d'ensemble" },
    { id: "flux",     label: "💸 Flux" },
    { id: "comptes",  label: "🏦 Comptes" },
    { id: "forecast", label: "🔮 Prévisionnel" },
    { id: "fiscal",   label: "📅 Obligations fiscales" },
  ];

  return (
    <div style={{ padding: "0 0 40px" }}>
      {toast && (
        <div style={{ position: "fixed", top: 20, right: 20, zIndex: 9999, padding: "12px 20px", borderRadius: 10, background: toast.type === "error" ? "#ef4444" : "#1a9e72", color: "#fff", fontWeight: 700, fontSize: 13 }}>{toast.msg}</div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#111827" }}>🏦 Trésorerie</h2>
          <div style={{ fontSize: 12, color: "#4b5563", marginTop: 2 }}>{currentSociete}</div>
        </div>
        <div style={{ background: "#ffffff", borderRadius: 12, padding: "10px 20px", textAlign: "right" }}>
          <div style={{ fontSize: 11, color: "#4b5563" }}>Solde consolidé</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: totalBalance >= 0 ? "#1a9e72" : "#ef4444" }}>{fmt(totalBalance)} €</div>
        </div>
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: "1px solid #e5e7eb" }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ ...S.btn, background: "transparent", color: tab === t.id ? "#1a9e72" : "#4b5563", borderBottom: `2px solid ${tab === t.id ? "#1a9e72" : "transparent"}`, borderRadius: 0, padding: "8px 14px 10px", fontSize: 12 }}>{t.label}</button>
        ))}
      </div>

      {/* ── Overview ── */}
      {tab === "overview" && (
        <div>
          <div style={{ display: "flex", gap: 16, marginBottom: 24, flexWrap: "wrap" }}>
            {[
              { label: "Solde total", value: fmt(totalBalance) + " €", color: totalBalance >= 0 ? "#1a9e72" : "#ef4444" },
              { label: "Nb de comptes", value: String(accounts.length), color: "#111827" },
              { label: "Comptes actifs", value: String(accounts.filter(a => a.status !== "fermé").length || accounts.length), color: "#185fa5" },
              { label: "Devise principale", value: "EUR €", color: "#4b5563" },
            ].map(k => (
              <div key={k.label} style={{ ...S.kpi, minWidth: 150 }}>
                <div style={{ fontSize: 11, color: "#4b5563", marginBottom: 6 }}>{k.label}</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: k.color }}>{k.value}</div>
              </div>
            ))}
          </div>

          <div style={S.card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Flux de trésorerie — 6 derniers mois</div>
            <ResponsiveContainer width="100%" height={220}>
              <ComposedChart data={monthlyChart} margin={{ left: 0, right: 10 }}>
                <XAxis dataKey="month" tick={{ fill: "#4b5563", fontSize: 11 }} />
                <YAxis tick={{ fill: "#4b5563", fontSize: 11 }} tickFormatter={fmtShort} />
                <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #334155", borderRadius: 8 }} formatter={v => fmt(v) + " €"} />
                <Legend />
                <Bar dataKey="entrees" name="Entrées" fill="#1a9e72" radius={[3, 3, 0, 0]} />
                <Bar dataKey="sorties" name="Sorties" fill="#ef4444" radius={[3, 3, 0, 0]} />
                <Line type="monotone" dataKey="solde" name="Solde net" stroke="#f59e0b" strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          {accounts.length > 0 && (
            <div style={{ ...S.card, marginTop: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Comptes bancaires</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {accounts.map(a => (
                  <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 16, padding: "12px 16px", background: "#f9fafb", borderRadius: 10 }}>
                    <span style={{ fontSize: 24 }}>🏦</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: "#111827", fontWeight: 600, fontSize: 13 }}>{a.bank_name || a.name}</div>
                      <div style={{ color: "#4b5563", fontSize: 11 }}>{maskIBAN(a.iban || a.account_number)} · {a.account_type || "Courant"}</div>
                    </div>
                    <div style={{ fontWeight: 800, color: parseFloat(a.balance || a.current_balance || 0) >= 0 ? "#1a9e72" : "#ef4444", fontSize: 16 }}>
                      {fmt(a.balance || a.current_balance || 0)} €
                    </div>
                    <button onClick={() => syncAccount(a.id)} style={{ ...S.btn, padding: "4px 10px", background: "rgba(24,95,165,0.2)", color: "#60a5fa", fontSize: 12 }}>🔄</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Flux ── */}
      {tab === "flux" && (
        <div>
          <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "flex-end" }}>
            <div>
              <label style={{ display: "block", color: "#4b5563", fontSize: 11, marginBottom: 4 }}>Du</label>
              <input type="date" value={txFilter.from} onChange={e => setTxFilter(f => ({ ...f, from: e.target.value }))} style={{ ...S.input, width: 150 }} />
            </div>
            <div>
              <label style={{ display: "block", color: "#4b5563", fontSize: 11, marginBottom: 4 }}>Au</label>
              <input type="date" value={txFilter.to} onChange={e => setTxFilter(f => ({ ...f, to: e.target.value }))} style={{ ...S.input, width: 150 }} />
            </div>
            <button onClick={loadTransactions} style={{ ...S.btn, background: "#185fa5", color: "#fff" }}>🔍 Filtrer</button>
            <button onClick={exportCSV} style={{ ...S.btn, background: "#f9fafb", color: "#4b5563" }}>⬇️ CSV</button>
          </div>

          {loading ? <div style={{ textAlign: "center", padding: 40, color: "#4b5563" }}>Chargement…</div> : (
            <div style={S.card}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                    {["Date", "Description", "Type", "Montant", "Référence"].map(h => (
                      <th key={h} style={{ padding: "10px 12px", textAlign: "left", color: "#4b5563", fontWeight: 600, fontSize: 11, textTransform: "uppercase" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {transactions.map((t, i) => {
                    const amt = parseFloat(t.amount || 0);
                    return (
                      <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ padding: "10px 12px", color: "#4b5563" }}>{(t.date || t.created_at || "").slice(0, 10)}</td>
                        <td style={{ padding: "10px 12px", color: "#111827" }}>{t.description || t.label || "—"}</td>
                        <td style={{ padding: "10px 12px" }}>
                          <span style={{ fontSize: 11, padding: "2px 7px", borderRadius: 20, fontWeight: 700, background: amt >= 0 ? "rgba(26,158,114,0.15)" : "rgba(239,68,68,0.15)", color: amt >= 0 ? "#1a9e72" : "#ef4444" }}>
                            {amt >= 0 ? "Entrée" : "Sortie"}
                          </span>
                        </td>
                        <td style={{ padding: "10px 12px", fontWeight: 700, color: amt >= 0 ? "#1a9e72" : "#ef4444" }}>{amt >= 0 ? "+" : ""}{fmt(amt)} €</td>
                        <td style={{ padding: "10px 12px", color: "#4b5563", fontSize: 12 }}>{t.reference || "—"}</td>
                      </tr>
                    );
                  })}
                  {transactions.length === 0 && <tr><td colSpan={5} style={{ padding: 30, textAlign: "center", color: "#4b5563" }}>Aucune transaction sur cette période</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Comptes ── */}
      {tab === "comptes" && (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
            <button onClick={() => setShowAddAccount(!showAddAccount)} style={{ ...S.btn, background: "#1a9e72", color: "#fff" }}>+ Ajouter un compte</button>
          </div>

          {showAddAccount && (
            <div style={{ ...S.card, marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Nouveau compte bancaire</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                {[
                  { label: "Nom de la banque", key: "bank_name" },
                  { label: "IBAN", key: "iban" },
                  { label: "Solde initial (€)", key: "initial_balance", type: "number" },
                ].map(f => (
                  <div key={f.key}>
                    <label style={{ display: "block", color: "#4b5563", fontSize: 11, marginBottom: 4 }}>{f.label}</label>
                    <input type={f.type || "text"} value={accountForm[f.key]} onChange={e => setAccountForm(o => ({ ...o, [f.key]: e.target.value }))} style={S.input} />
                  </div>
                ))}
                <div>
                  <label style={{ display: "block", color: "#4b5563", fontSize: 11, marginBottom: 4 }}>Type de compte</label>
                  <select value={accountForm.account_type} onChange={e => setAccountForm(o => ({ ...o, account_type: e.target.value }))} style={S.input}>
                    <option value="courant">Compte courant</option>
                    <option value="épargne">Compte épargne</option>
                    <option value="professionnel">Compte professionnel</option>
                  </select>
                </div>
              </div>
              <div style={{ display: "flex", gap: 10 }}>
                <button onClick={addAccount} style={{ ...S.btn, background: "#1a9e72", color: "#fff" }}>✅ Enregistrer</button>
                <button onClick={() => setShowAddAccount(false)} style={{ ...S.btn, background: "#f9fafb", color: "#4b5563" }}>Annuler</button>
              </div>
            </div>
          )}

          {accounts.length === 0 ? (
            <div style={{ textAlign: "center", padding: 60 }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}>🏦</div>
              <div style={{ color: "#111827", fontWeight: 700, marginBottom: 8 }}>Aucun compte bancaire</div>
              <div style={{ color: "#4b5563", fontSize: 13 }}>Ajoutez vos comptes pour suivre votre trésorerie.</div>
            </div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 }}>
              {accounts.map(a => (
                <div key={a.id} style={S.card}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <span style={{ fontSize: 28 }}>🏦</span>
                      <div>
                        <div style={{ color: "#111827", fontWeight: 700, fontSize: 14 }}>{a.bank_name || a.name}</div>
                        <div style={{ color: "#4b5563", fontSize: 11 }}>{a.account_type || "Courant"}</div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => syncAccount(a.id)} style={{ ...S.btn, padding: "4px 8px", background: "rgba(24,95,165,0.2)", color: "#60a5fa", fontSize: 11 }}>🔄</button>
                      <button onClick={() => deleteAccount(a.id)} style={{ ...S.btn, padding: "4px 8px", background: "rgba(239,68,68,0.15)", color: "#ef4444", fontSize: 11 }}>✕</button>
                    </div>
                  </div>
                  <div style={{ color: "#4b5563", fontSize: 12, marginBottom: 12 }}>{maskIBAN(a.iban || a.account_number)}</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: parseFloat(a.balance || a.current_balance || 0) >= 0 ? "#1a9e72" : "#ef4444" }}>
                    {fmt(a.balance || a.current_balance || 0)} €
                  </div>
                  <div style={{ color: "#4b5563", fontSize: 11, marginTop: 4 }}>Sync : {a.last_sync ? new Date(a.last_sync).toLocaleDateString("fr-FR") : "Jamais"}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Forecast ── */}
      {tab === "forecast" && (
        <div>
          <div style={{ display: "flex", gap: 12, marginBottom: 20, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ display: "flex", gap: 4 }}>
              {[30, 60, 90].map(d => (
                <button key={d} onClick={() => setForecastDays(d)} style={{ ...S.btn, background: forecastDays === d ? "#185fa5" : "#f9fafb", color: forecastDays === d ? "#fff" : "#4b5563" }}>{d} jours</button>
              ))}
            </div>
            <div style={{ display: "flex", gap: 4 }}>
              {[["optimiste", "#1a9e72"], ["realiste", "#185fa5"], ["pessimiste", "#ef4444"]].map(([s, c]) => (
                <button key={s} onClick={() => setScenario(s)} style={{ ...S.btn, background: scenario === s ? c : "#f9fafb", color: scenario === s ? "#fff" : "#4b5563", textTransform: "capitalize" }}>{s}</button>
              ))}
            </div>
          </div>

          <div style={S.card}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Projection solde — {forecastDays} jours</div>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={forecastData} margin={{ left: 0, right: 10 }}>
                <defs>
                  <linearGradient id="gf" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#185fa5" stopOpacity={0.3} /><stop offset="95%" stopColor="#185fa5" stopOpacity={0} /></linearGradient>
                </defs>
                <XAxis dataKey="semaine" tick={{ fill: "#4b5563", fontSize: 11 }} />
                <YAxis tick={{ fill: "#4b5563", fontSize: 11 }} tickFormatter={fmtShort} />
                <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #334155", borderRadius: 8 }} formatter={v => fmt(v) + " €"} />
                <Area type="monotone" dataKey="solde" name="Solde prévu" stroke="#185fa5" fill="url(#gf)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div style={{ ...S.card, marginTop: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 16 }}>Détail hebdomadaire</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead><tr style={{ borderBottom: "1px solid #e5e7eb" }}>
                {["Semaine", "Entrées prévues", "Sorties prévues", "Solde prévisionnel"].map(h => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left", color: "#4b5563", fontWeight: 600, fontSize: 11, textTransform: "uppercase" }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {forecastData.map((w, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={{ padding: "8px 12px", color: "#4b5563", fontWeight: 600 }}>{w.semaine}</td>
                    <td style={{ padding: "8px 12px", color: "#1a9e72", fontWeight: 700 }}>+{fmt(w.entrees)} €</td>
                    <td style={{ padding: "8px 12px", color: "#ef4444", fontWeight: 700 }}>-{fmt(w.sorties)} €</td>
                    <td style={{ padding: "8px 12px", fontWeight: 800, color: w.solde >= 0 ? "#1a9e72" : "#ef4444" }}>{fmt(w.solde)} €</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 12, padding: 10, background: "rgba(100,116,139,0.1)", borderRadius: 8, fontSize: 12, color: "#4b5563" }}>
              Prévision estimée sur base des flux historiques. Ne constitue pas un conseil financier.
            </div>
          </div>
        </div>
      )}

      {/* ── Fiscal ── */}
      {tab === "fiscal" && (
        <div>
          <div style={{ fontSize: 13, color: "#4b5563", marginBottom: 20 }}>Prochaines échéances fiscales — calendrier fiscal français.</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {fiscalEvents.map((e, i) => {
              const urgencyColor = e.diff < 7 ? "#ef4444" : e.diff < 30 ? "#f59e0b" : "#1a9e72";
              return (
                <div key={i} style={{ ...S.card, display: "flex", alignItems: "center", gap: 20, padding: "16px 20px" }}>
                  <div style={{ width: 56, height: 56, borderRadius: 12, background: urgencyColor + "22", border: `2px solid ${urgencyColor}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                    <span style={{ fontSize: 11, fontWeight: 800, color: urgencyColor, textAlign: "center" }}>{e.diff < 0 ? "PASSÉ" : e.diff + "j"}</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: "#111827", fontWeight: 700, fontSize: 14 }}>{e.name}</div>
                    <div style={{ color: "#4b5563", fontSize: 12, marginTop: 2 }}>Échéance : {e.dateStr}</div>
                  </div>
                  <button onClick={() => showToast(`Rappel créé pour ${e.name}`)} style={{ ...S.btn, background: "rgba(24,95,165,0.2)", color: "#60a5fa", fontSize: 12 }}>🔔 Rappel</button>
                </div>
              );
            })}
          </div>
          <div style={{ ...S.card, marginTop: 24 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 12 }}>Accès rapides</div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button onClick={() => onNavigate?.("tva")} style={{ ...S.btn, background: "rgba(239,68,68,0.15)", color: "#f87171" }}>🌍 Déclaration TVA</button>
              <button onClick={() => onNavigate?.("comptabilite")} style={{ ...S.btn, background: "rgba(24,95,165,0.15)", color: "#60a5fa" }}>📊 Comptabilité</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
